// Marcador "foto do veículo" no mapa (pedido 2026-09-29: "círculo com a
// imagem do carro, igual no mapa de compartilhar localização"). O deck.gl
// IconLayer só desenha imagens, então a foto é recortada num círculo com
// borda na cor do status num <canvas> e vira um data URI — cache por
// foto+cor, gerado uma vez só. A seta de direção é outra imagem (fixa por
// cor) girada pelo próprio IconLayer, pra foto nunca ficar de cabeça pra
// baixo.

// Resolução do canvas: o círculo aparece a 84px na tela (132px/3x ficou
// grande demais, ajustado em 2026-09-29) — 256 pra não borrar em tela
// retina. O desenho usa coordenadas de 128 escaladas (DRAW_SCALE).
export const PHOTO_MARKER_PX = 256;
const DRAW_SCALE = PHOTO_MARKER_PX / 128;
// Tamanho na tela do círculo (px) no zoom de rua. De longe ficava grande
// demais (pedido 2026-09-29) — photoMarkerSizeForZoom encolhe até o
// tamanho do ícone normal (44px) com o mapa afastado.
export const PHOTO_MARKER_SIZE = 84;
const PHOTO_MIN_SIZE = 40;
const ZOOM_FAR = 11;
const ZOOM_NEAR = 16;

export function photoMarkerSizeForZoom(zoom: number): number {
  const t = Math.min(1, Math.max(0, (zoom - ZOOM_FAR) / (ZOOM_NEAR - ZOOM_FAR)));
  // Arredonda de 4 em 4px: evita redesenhar a camada a cada fração de zoom.
  return Math.round((PHOTO_MIN_SIZE + t * (PHOTO_MARKER_SIZE - PHOTO_MIN_SIZE)) / 4) * 4;
}

const circleCache = new Map<string, string>();
const pending = new Set<string>();
const failed = new Set<string>();

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Foto vem da API (outra origem, com CORS liberado) — sem isto o
    // canvas fica "tainted" e toDataURL lança erro.
    if (!src.startsWith("data:")) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function drawCircle(img: HTMLImageElement | null, ring: string, contain: boolean): string {
  const S = 128;
  const c = S / 2;
  const canvas = document.createElement("canvas");
  canvas.width = PHOTO_MARKER_PX;
  canvas.height = PHOTO_MARKER_PX;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(DRAW_SCALE, DRAW_SCALE);
  const outer = 52;
  const inner = 44;

  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,.45)";
  ctx.shadowBlur = 10;
  ctx.shadowOffsetY = 3;
  ctx.beginPath();
  ctx.arc(c, c, outer, 0, Math.PI * 2);
  ctx.fillStyle = ring;
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.beginPath();
  ctx.arc(c, c, inner, 0, Math.PI * 2);
  ctx.clip();
  ctx.fillStyle = contain ? "#f1f5f9" : "#0f172a";
  ctx.fillRect(c - inner, c - inner, inner * 2, inner * 2);
  if (img) {
    const d = inner * 2;
    const scale = contain
      ? Math.min(d / img.width, d / img.height) * 0.92
      : Math.max(d / img.width, d / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, c - w / 2, c - h / 2, w, h);
  }
  ctx.restore();

  // Filete branco entre a borda colorida e a foto — destaca em qualquer mapa.
  ctx.beginPath();
  ctx.arc(c, c, inner, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,.9)";
  ctx.lineWidth = 2;
  ctx.stroke();
  return canvas.toDataURL("image/png");
}

/**
 * Data URI do círculo, ou null enquanto a imagem ainda carrega (o
 * chamador desenha o ícone normal nesse meio-tempo e redesenha quando
 * `onReady` é chamado). `contain` = ícone 3D (sem foto): cabe inteiro no
 * círculo em vez de preencher.
 */
const placeholderCache = new Map<string, string>();

/**
 * Círculo vazio (borda de status + fundo escuro), desenhado na hora sem
 * esperar imagem nenhuma. Bug 2026-10-01: enquanto a foto ainda carregava,
 * o mapa mostrava o ícone 3D (ex.: a moto) e só depois trocava pra foto —
 * agora o lugar já aparece como círculo e a foto entra dentro dele.
 */
export function placeholderCircleIcon(ring: string): string {
  const hit = placeholderCache.get(ring);
  if (hit) return hit;
  const url = drawCircle(null, ring, false);
  placeholderCache.set(ring, url);
  return url;
}

export function photoCircleIcon(src: string, ring: string, contain: boolean, onReady: () => void): string | null {
  const key = `${ring}|${contain ? 1 : 0}|${src}`;
  const hit = circleCache.get(key);
  if (hit) return hit;
  if (failed.has(key)) return null;
  if (pending.has(key)) return placeholderCircleIcon(ring);
  pending.add(key);
  loadImage(src)
    .then((img) => {
      circleCache.set(key, drawCircle(img, ring, contain));
      onReady();
    })
    .catch(() => failed.add(key))
    .finally(() => pending.delete(key));
  return placeholderCircleIcon(ring);
}

export function photoFailed(src: string, ring: string): boolean {
  return failed.has(`${ring}|0|${src}`);
}

const arrowCache = new Map<string, string>();

// Seta na borda do círculo, apontando pra cima (norte); o IconLayer gira.
// Desenhada no mesmo tamanho de canvas do círculo, mas exibida maior
// (ARROW_SCALE) pra ponta ficar pra fora da borda.
export const ARROW_SCALE = 1.3;

export function headingArrowIcon(color: string): string {
  const hit = arrowCache.get(color);
  if (hit) return hit;
  const canvas = document.createElement("canvas");
  canvas.width = PHOTO_MARKER_PX;
  canvas.height = PHOTO_MARKER_PX;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(DRAW_SCALE, DRAW_SCALE);
  ctx.beginPath();
  ctx.moveTo(64, 3);
  ctx.lineTo(80, 24);
  ctx.lineTo(64, 18);
  ctx.lineTo(48, 24);
  ctx.closePath();
  ctx.lineJoin = "round";
  ctx.lineWidth = 3;
  ctx.strokeStyle = "#fff";
  ctx.fillStyle = color;
  ctx.stroke();
  ctx.fill();
  const uri = canvas.toDataURL("image/png");
  arrowCache.set(color, uri);
  return uri;
}
